/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getUsersByID, getPlayerBalance } from "../../store/slices/user";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import kkpIcon from "../../assets/bankicon/069.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";
import { SocketContext } from "../../SocketContext";
import styles from "./Withdraw.module.css";

export default function Withdraw() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { singleUsers, playerBalance } = useSelector((s) => s.user);
  const socket = useContext(SocketContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const account = Array.isArray(singleUsers) ? singleUsers[0] : null;

  React.useEffect(() => {
    dispatch(getPlayerBalance(user?.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Determine user id (from current user slice, localStorage, or account)
  const storedId =
    user?.id || // prefer authenticated user id
    account?.id ||
    (typeof window !== "undefined" ? localStorage.getItem("userId") : null);

  const getBankLogo = (bankCode) => {
    const map = {
      true: truemoneyIcon,
      "014": scbIcon,
      "006": ktbIcon,
      "004": kbankIcon,
      "002": bblIcon,
      "011": ttbIcon,
      "030": gsbIcon,
      "025": krungsriIcon,
      "034": baacIcon,
      "024": uobIcon,
      "033": ghbIcon,
      "022": cimbIcon,
      "066": islamIcon,
      "069": kkpIcon,
      promptpay: promptpayIcon,
    };
    return map[bankCode] || scbIcon;
  };

  const getBankName = (bankCode) => {
    const map = {
      true: "ทรูมันนี่วอลเล็ท",
      "014": "ธนาคารไทยพาณิชย์",
      "006": "ธนาคารกรุงไทย",
      "004": "ธนาคารกสิกรไทย",
      "002": "ธนาคารกรุงเทพ",
      "011": "ธนาคารทีทีบี",
      "030": "ธนาคารออมสิน",
      "025": "ธนาคารกรุงศรี",
      "034": "ธนาคารธ.ก.ส.",
      "024": "ธนาคารUOB",
      "033": "ธนาคารอาคารสงเคราะห์",
      "022": "ธนาคารCIMB",
      "066": "ธนาคารอิสลาม",
      "069": "ธนาคารเกียรตินาคิน",
      promptpay: "พร้อมเพย์",
    };
    return map[bankCode] || "ธนาคาร";
  };

  // Fetch user data from API
  useEffect(() => {
    if (storedId) dispatch(getUsersByID({ id: storedId }));
  }, [storedId, dispatch]);

  const handleWithdraw = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    setMessage("");
    setShowError(false);

    try {
      // Send URL-encoded body like the frontend example
      const body = new URLSearchParams();
      body.append("message", "withdraw request");

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/8hwe8vhevv/requestwithdraw/${user.id}`,
        body,
      );

      if (response.data.success === true) {
        socket?.emit("withdraw-request", { message: "withdraw request" }, user);
        setMessage("แจ้งถอนเงินเรียบร้อย กรุณารอการอนุมัติจากเจ้าหน้าที่");
        setTimeout(() => {
          setMessage("");
        }, 5000);
      } else {
        setErrorMessage(response.data.message);
        setShowError(true);
        setMessage("กรุณาทำยอดให้ครบตามเงื่อนไขหรือเคลียร์เดิมพันคงค้าง");
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      setErrorMessage("ไม่สามารถแจ้งถอนเงินได้ โปรดติดต่อเจ้าหน้าที่");
      setShowError(true);
      setMessage("เกิดข้อผิดพลาดในการเชื่อมต่อ");
    } finally {
      setTimeout(() => {
        dispatch(getPlayerBalance(user?.id));
        setIsProcessing(false);
      }, 2000);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroEmoji}>💸</div>
        <h2 className={styles.heroTitle}>ถอนเงิน</h2>
        <p className={styles.heroDesc}>
          ดำเนินการภายใน 5-10 นาที • มั่นคงปลอดภัย 100%
        </p>
      </div>

      {/* ── Balance Card ── */}
      <div className={styles.balanceCard}>
        <span className={styles.balanceLabel}>
          <Icon icon="solar:wallet-bold-duotone" width={16} height={16} />
          ยอดเงินคงเหลือ
        </span>
        <span className={styles.balanceValue}>
          {parseInt(playerBalance)}
          <span className={styles.balanceCurrency}>THB</span>
        </span>
      </div>

      {/* ── Bank Info Card ── */}
      <div className={styles.bankCard}>
        <div className={styles.bankCardHeader}>
          <div className={styles.bankCardIcon}>
            <Icon icon="solar:bank-bold-duotone" width={22} height={22} />
          </div>
          <div className={styles.bankCardHeaderText}>
            <div className={styles.bankCardTitle}>บัญชีถอนเงิน</div>
            <div className={styles.bankCardSubtitle}>
              โอนเข้าบัญชีที่ท่านผูกไว้โดยอัตโนมัติ
            </div>
          </div>
        </div>

        <div className={styles.bankCardBody}>
          {account?.book_number ? (
            <div className={styles.bankInfo}>
              <div className={styles.bankLogo}>
                <img
                  src={getBankLogo(account?.bank_name)}
                  alt={getBankName(account?.bank_name)}
                  className={styles.bankLogoImg}
                />
              </div>
              <div className={styles.bankDetails}>
                <p className={styles.bankName}>
                  {getBankName(account?.bank_name)}
                </p>
                <p className={styles.bankAccount}>{account?.book_number}</p>
                <p className={styles.bankOwner}>
                  ชื่อบัญชี: {account?.book_name}
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.noBankCard}>
              <div className={styles.noBankIcon}>
                <Icon icon="solar:bank-bold-duotone" width={26} height={26} />
              </div>
              <h4 className={styles.noBankTitle}>ยังไม่มีบัญชีธนาคาร</h4>
              <p className={styles.noBankDesc}>
                กรุณาเพิ่มข้อมูลบัญชีธนาคารเพื่อทำธุรกรรม
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Status Messages ── */}
      {message && (
        <div
          className={
            message.includes("เรียบร้อย")
              ? styles.successMsg
              : styles.warningMsg
          }
        >
          <Icon
            icon={
              message.includes("เรียบร้อย")
                ? "solar:check-circle-bold"
                : "solar:info-circle-bold"
            }
            width={20}
            height={20}
            className={styles.msgIcon}
          />
          <span>{message}</span>
        </div>
      )}

      {showError && errorMessage && (
        <div className={styles.errorMsg}>
          <Icon
            icon="solar:danger-triangle-bold"
            width={20}
            height={20}
            className={styles.msgIcon}
          />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* ── Loading ── */}
      {isProcessing && (
        <div className={styles.loadingBox}>
          <Icon
            icon="solar:loading-bold-duotone"
            width={20}
            height={20}
            className={styles.spinIcon}
          />
          <span>กำลังดำเนินการ...</span>
        </div>
      )}

      {/* ── Submit ── */}
      <button
        className={styles.submitBtn}
        onClick={handleWithdraw}
        disabled={isProcessing}
      >
        <Icon icon="solar:wallet-money-bold-duotone" width={20} height={20} />
        {isProcessing ? "กำลังดำเนินการ..." : "ยืนยันถอนเงิน"}
      </button>

      {/* ── Terms ── */}
      <div className={styles.termsCard}>
        <div className={styles.termsHeader}>
          <Icon icon="solar:info-circle-bold" width={16} height={16} />
          <span className={styles.termsTitle}>เงื่อนไขการถอน</span>
        </div>
        <div className={styles.termsList}>
          <div className={styles.termsItem}>
            <Icon
              icon="solar:alt-arrow-right-bold"
              width={14}
              height={14}
              className={styles.termsBullet}
            />
            <span>ท่านต้องทำตามเทิร์นที่กำหนดก่อนแจ้งถอนเงิน</span>
          </div>
          <div className={styles.termsItem}>
            <Icon
              icon="solar:alt-arrow-right-bold"
              width={14}
              height={14}
              className={styles.termsBullet}
            />
            <span>ระบบจะดำเนินการภายใน 5-10 นาที</span>
          </div>
          <div className={styles.termsItem}>
            <Icon
              icon="solar:alt-arrow-right-bold"
              width={14}
              height={14}
              className={styles.termsBullet}
            />
            <span>มั่นคงปลอดภัย 100%</span>
          </div>
          <div className={styles.termsItem}>
            <Icon
              icon="solar:alt-arrow-right-bold"
              width={14}
              height={14}
              className={styles.termsBullet}
            />
            <span>
              ระบบจะถอนไปยังบัญชีที่ท่านผูกไว้
              หากมีการเปลี่ยนแปลงกรุณาแจ้งแอดมิน
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
